import { Avatar, Link, styled, Tooltip, Typography } from "@mui/material";
import { BrokenImage } from "@mui/icons-material";
import { motion } from "framer-motion";

import { useTranslation } from "react-i18next";

import { MeasureImpact } from "types/types";

import { Image } from "components/img/Image";

interface Props {
  measureImpact: MeasureImpact;
  onSelectMeasure: (measure: MeasureImpact) => void;
}

export function MeasureContentItem(props: Props) {
  const { measureImpact, onSelectMeasure } = props;
  const { t } = useTranslation();

  return (
    <StyledPaper
      layout
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div
        className="img-grid-item"
        onClick={() => onSelectMeasure(measureImpact)}
      >
        <div className="overlay-icon-div"></div>
        <Image
          defaultIcon={<BrokenImage fontSize="large" />}
          src={`${measureImpact.measure.imageUrl}`}
          height={110}
        />
      </div>
      <div className="content-grid-item">
        <Typography variant="h3" component="h3">
          {measureImpact.measure.name}
        </Typography>
        {measureImpact.measure.key && (
          <Link
            href={measureImpact.measure.infoPage}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t("label.moreInfo")}
          </Link>
        )}
      </div>
      <div
        className="theme-grid-item"
        onClick={() => onSelectMeasure(measureImpact)}
      >
        {measureImpact.themeScoreList.map((themeScore) => (
          <Tooltip title={themeScore.theme.name} key={themeScore.theme.id}>
            <Typography
              component="div"
              variant="h2"
              className="theme-title"
              noWrap
            >
              <Avatar src={themeScore.theme.iconUrl} variant="square" />
              {themeScore.score}
            </Typography>
          </Tooltip>
        ))}
      </div>
    </StyledPaper>
  );
}

const StyledPaper = styled(motion.div)`
  min-width: 0;
  display: grid;
  grid-template-areas:
    "img content"
    "theme content";
  grid-template-rows: 110px minmax(0, auto);
  grid-template-columns: 150px minmax(0, auto);

  .MuiTypography-root {
    overflow: hidden;
  }

  &.active {
    background-color: ${({ theme }) => theme.palette.grey[100]};
  }

  :hover {
    background-color: ${({ theme }) => theme.palette.grey[100]};
  }

  .img-grid-item {
    grid-area: img;
    position: relative;
    cursor: pointer;
    isolation: isolate;

    img {
      z-index: 1;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .overlay-icon-div {
      z-index: 2;
      position: absolute;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.6) 70%
      );
      .MuiSvgIcon-root {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: ${({ theme }) => theme.palette.success.main};
        background-color: transparent;
        &.inactive {
          display: none;
        }
      }
    }
  }
  .content-grid-item {
    grid-area: content;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: ${({ theme }) => theme.spacing(1)};
    overflow-wrap: break-word;
  }
  .theme-grid-item {
    cursor: pointer;
    grid-area: theme;
    display: flex;
    flex-direction: row;
    margin-left: ${({ theme }) => theme.spacing(-0.5)};
  }

  .theme-title {
    display: inline-flex;
    align-items: center;
    .MuiAvatar-img {
      mix-blend-mode: multiply;
      width: 20px;
      height: 20px;
    }
  }

  &.MuiTooltip-root {
    font-size: 13px;
    margin-top: 0;
  }
`;
